'use client';

/**
 * The planner's action bar: one row, pinned to the bottom (fixed to the
 * screen on phones).
 *
 *   Not saved / changed:  [ Save trip ━━━━━━━━━ ]  [⇪ Share]  [⋯]
 *   Saved:                [ ⇪ Share ━━━━━━━━━━━━━━━━━━━━ ]  [⋯]
 *
 *   Save   the one primary until the trip is saved. Saving never needs an
 *          account (guests save to this device).
 *   Share  a read-only link. Once the trip is saved there is nothing left to
 *          save, so Share takes the whole primary slot. "Saved · just now"
 *          is said once, by the chip beside the trip name. Share saves
 *          first when it needs to.
 *   ⋯      Take it with you: GPX · KML · Print (guests see one export row
 *          with a "Sign in" button on it: the gate says how to open it)
 *          Trip: Plan my nights (signed in) · Duplicate · New trip
 *          ──── Clear trip / Delete trip: red, last, both ask first.
 *          Drop a pin is a map tool (the map's control stack); Ask AI sits
 *          beside the add-a-stop box.
 *
 * The menu dims what's behind it, so it reads as on top, not part of the
 * list. While the share sheet is up the bar steps out of sight: one Share
 * in view, never two.
 *
 * "New trip" on a trip that isn't saved offers three ways out: save it
 * first (the default), throw it away (red), or keep planning (a quiet link).
 *
 * No sign-in line over the bar: a guest's first save says what an account
 * adds, once, in the toast. The status line ("Removed Port Fairy · Undo")
 * gets its own row above the bar, so it never sits over a stop. Plain
 * confirmations stay on one line and leave by themselves after 2s.
 */
import React from 'react';
import { apiExportTrip } from '../../utils/tripApi';
import { getUserToken } from '../../utils/reviewerApi';
import type { StoredTrip } from '../../utils/tripStorage';
import TripShareButton, { type SharePreview } from '../TripShareButton';
import {
  IconChat,
  IconCheck,
  IconCopy,
  IconDownload,
  IconFilePlus,
  IconLock,
  IconMoon,
  IconMore,
  IconPinPlus,
  IconPrinter,
  IconTrash,
  IconX,
} from './icons';
import type { TripStatusMessage, TripStop } from './types';
import { Button, Dialog, Menu, Sheet, type MenuItem } from './ui';

// Menu row icons: 20px, the weight of the 15px row text beside them.
const ROW_ICON = 'h-5 w-5';

export const STATUS_TONE: Record<TripStatusMessage['tone'], string> = {
  success: 'border-tp-ok/25 bg-tp-ok-soft text-tp-ok',
  warning: 'border-tp-warn-line bg-tp-warn-soft text-tp-warn',
  error: 'border-tp-danger/25 bg-tp-danger-soft text-tp-danger',
};

/** "Melbourne to Sydney" → "melbourne-to-sydney.gpx" */
export function exportFilename(stops: Pick<TripStop, 'name'>[], fmt: 'gpx' | 'kml'): string {
  const a = stops[0]?.name || 'trip';
  const b = stops.length > 1 ? stops[stops.length - 1]?.name || '' : '';
  const base =
    `${a}${b ? '-to-' + b : ''}`
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'trip';
  return `${base}.${fmt}`;
}

/**
 * Corridor search args. Stops with coordinates are passed as "lat,lng":
 * business names like "BIG4 Karuah Jetty Holiday Park" don't geocode
 * server-side (Nominatim fallback), which returned 0 results and dumped the
 * map at the continent view. The readable name is passed separately so the
 * search box still shows words, not numbers.
 */
export function corridorSearchArgs(
  stops: TripStop[],
  routeName: string
): { start: string; end: string; name: string; mids: string[] } | null {
  const first = stops[0];
  const last = stops[stops.length - 1];
  if (!first?.name || !last?.name) return null;
  const locOf = (s: TripStop): string =>
    typeof s.lat === 'number' && typeof s.lng === 'number' ? `${s.lat},${s.lng}` : s.name;
  return {
    start: locOf(first),
    end: locOf(last),
    name: routeName || `${first.name} to ${last.name}`,
    mids: stops.slice(1, -1).map(locOf).filter(Boolean),
  };
}

export interface TripOverflowProps {
  hasTripStops: boolean;
  activeTripId: string | null;
  /** Kept for callers: saved trips now live in the trip-name menu. */
  savedTrips?: StoredTrip[];
  onLoadTrip?: (id: string) => void;
  /** Opens the "New trip" confirm (the bar owns the dialog). */
  onClearTrip: () => void;
  /**
   * Opens the "Clear trip" confirm: the red last row of an unsaved trip
   * (a saved one gets Delete there instead).
   */
  onClearStops?: () => void;
  /** Copy this trip into a new, unsaved one. */
  onDuplicate?: () => void;
  /** Present only for a saved trip. */
  onDelete?: () => void;
  /** Signed in: open (or close) Plan my nights. */
  onPlanNights?: () => void;
  nightsOpen?: boolean;
  /** GPX / KML / print (buildExportItems). */
  exportItems?: MenuItem[];
  /** Tools listed after the export items (Ask AI when there's no box for it). */
  toolItems?: MenuItem[];
  /** Kept for callers; rename is a click on the trip name now. */
  onRename?: () => void;
  /** Kept for callers; Share has its own button in the bar. */
  onShare?: () => void;
  isSaved?: boolean;
}

/**
 * The ⋯ menu's items, in labelled groups: "Take it with you", "Trip", then
 * a divider and the one red row (Clear trip, or Delete trip once saved).
 * Pure, so the order and tones are testable.
 */
export function tripOverflowItems({
  hasTripStops,
  activeTripId,
  onClearTrip,
  onClearStops,
  onDuplicate,
  onDelete,
  onPlanNights,
  nightsOpen = false,
  exportItems = [],
  toolItems = [],
}: TripOverflowProps): MenuItem[] {
  if (!hasTripStops) return toolItems;
  const trip: MenuItem[] = [
    ...toolItems,
    ...(onPlanNights
      ? [
          {
            key: 'nights',
            label: nightsOpen ? 'Hide my nights' : 'Plan my nights',
            hint: nightsOpen ? 'Back to just the stops' : 'A camp where each day ends',
            icon: <IconMoon className={ROW_ICON} />,
            onSelect: onPlanNights,
          },
        ]
      : []),
    ...(onDuplicate
      ? [
          {
            key: 'duplicate',
            label: 'Duplicate',
            hint: 'A copy to try a different route',
            icon: <IconCopy className={ROW_ICON} />,
            onSelect: onDuplicate,
          },
        ]
      : []),
    {
      key: 'new',
      label: 'New trip',
      hint: 'Start again from a blank map',
      icon: <IconFilePlus className={ROW_ICON} />,
      onSelect: onClearTrip,
    },
  ].map((item, i) => (i === 0 ? { ...item, group: 'Trip' } : item));
  const red: MenuItem | null = onDelete
    ? {
        key: 'delete',
        label: 'Delete trip',
        hint: 'Removes it from your saved trips',
        icon: <IconTrash className={ROW_ICON} />,
        tone: 'danger',
        separated: true,
        onSelect: onDelete,
      }
    : onClearStops && !activeTripId
      ? {
          key: 'clear',
          label: 'Clear trip',
          hint: 'Removes every stop',
          icon: <IconTrash className={ROW_ICON} />,
          tone: 'danger',
          separated: true,
          onSelect: onClearStops,
        }
      : null;
  return [...exportItems, ...trip, ...(red ? [red] : [])];
}

/**
 * The gate on the guests' export row: a small lock right after the label.
 * The whole row is the button (it opens sign-in); the hint under the label
 * says so in words. No pill crowding the row.
 */
function SignInTag() {
  return (
    <span className="inline-flex flex-none items-center text-tp-muted" data-testid="signin-tag" title="Needs a free account">
      <IconLock className="h-3.5 w-3.5" />
      <span className="sr-only">(needs a free account)</span>
    </span>
  );
}

/**
 * GPX, KML and print. GPX/KML are built server-side from the saved trip, so
 * they need an account. Guests see ONE row for both, saying so up front (a
 * lock and "Sign in to download"); the click opens sign-in with a line
 * saying why. A signed-in trip that isn't saved yet is saved first.
 */
export function buildExportItems({
  tripStops,
  activeTripId,
  isLoggedIn,
  onSignIn,
  onPrint,
  ensureSaved,
  setTripStatusMessage,
}: {
  tripStops: TripStop[];
  activeTripId: string | null;
  /** Omitted → read from the stored token at click time. */
  isLoggedIn?: boolean;
  onSignIn: () => void;
  onPrint: () => void;
  /** Save first (signed in, unsaved). Resolves to the server id, or null. */
  ensureSaved?: () => Promise<string | null>;
  setTripStatusMessage: (msg: TripStatusMessage | null) => void;
  /** @deprecated One group now. */
  grouped?: boolean;
}): MenuItem[] {
  const guest = isLoggedIn === false;
  const askToSignIn = (what: string) => {
    onSignIn();
    setTripStatusMessage({ message: `Sign in to download ${what}. Your trip comes with you.`, tone: 'warning' });
  };
  const exportAs = async (fmt: 'gpx' | 'kml') => {
    if (guest || !getUserToken()) {
      askToSignIn(fmt.toUpperCase());
      return;
    }
    const id = ensureSaved ? await ensureSaved() : activeTripId;
    if (!id) {
      setTripStatusMessage({ message: 'Save your trip first. Then download it.', tone: 'warning' });
      return;
    }
    const ok = await apiExportTrip(id, fmt, { filename: exportFilename(tripStops, fmt) });
    setTripStatusMessage(
      ok
        ? { message: `${fmt.toUpperCase()} downloaded`, tone: 'success' }
        : { message: 'Couldn’t export just now. Try again in a sec.', tone: 'warning' }
    );
  };
  const print: MenuItem = {
    key: 'print',
    label: 'Print day sheet',
    hint: 'One page per day for the glovebox',
    icon: <IconPrinter className={ROW_ICON} />,
    onSelect: onPrint,
  };
  if (guest) {
    return [
      {
        key: 'export',
        group: 'Take it with you',
        label: 'Export GPX / KML',
        hint: 'Sign in to download, free',
        icon: <IconDownload className={ROW_ICON} />,
        badge: <SignInTag />,
        title: 'GPX for Garmin and most nav units, KML for Google Earth. Needs a free account.',
        onSelect: () => askToSignIn('GPX or KML'),
      },
      print,
    ];
  }
  return [
    {
      key: 'gpx',
      group: 'Take it with you',
      label: 'GPX file',
      hint: 'For Garmin, GaiaGPS and most nav units',
      icon: <IconDownload className={ROW_ICON} />,
      onSelect: () => void exportAs('gpx'),
    },
    {
      key: 'kml',
      label: 'KML file',
      hint: 'For Google Earth and My Maps',
      icon: <IconDownload className={ROW_ICON} />,
      onSelect: () => void exportAs('kml'),
    },
    print,
  ];
}

/** What starting over costs, from the real save state. Pure. */
export function newTripConfirmCopy({
  name,
  stopCount,
  activeTripId,
  isSaved,
}: {
  name: string;
  stopCount: number;
  activeTripId: string | null;
  isSaved: boolean;
}): { title: string; body: string; confirm: string; destructive: boolean; saveFirst?: string } {
  if (activeTripId && isSaved) {
    return {
      title: 'Start a new trip?',
      body: `“${name}” stays in your saved trips.`,
      confirm: 'Start a new trip',
      destructive: false,
    };
  }
  if (activeTripId) {
    return {
      title: 'Start a new trip?',
      body: `Your last save of “${name}” stays. The changes since then go unless you save them.`,
      confirm: 'Discard changes',
      destructive: true,
      saveFirst: 'Save changes, then start new',
    };
  }
  return {
    title: 'Start a new trip?',
    body: `“${name}” isn’t saved. Starting over clears ${stopCount === 1 ? 'its one stop' : `all ${stopCount} stops`}.`,
    confirm: 'Discard and start new',
    destructive: true,
    saveFirst: 'Save, then start new',
  };
}

/** The red "Clear trip": every stop goes, the trip stays open. Pure. */
export function clearTripConfirmCopy(stopCount: number): { title: string; body: string; confirm: string; destructive: boolean; saveFirst?: string } {
  return {
    title: 'Clear trip?',
    body:
      stopCount === 1
        ? 'This removes its one stop. It isn’t saved anywhere.'
        : `This removes all ${stopCount} stops. It isn’t saved anywhere.`,
    confirm: 'Clear trip',
    destructive: true,
  };
}

export interface TripActionsProps {
  tripStops: TripStop[];
  activeTripId: string | null;
  /** Kept for callers: saved trips live in the trip-name menu now. */
  savedTrips?: StoredTrip[];
  onLoadTrip?: (id: string) => void;
  /** Saved and unchanged since: the primary reads "Saved" and rests. */
  isSaved: boolean;
  /** The trip's name, for the confirm and the share sheet. */
  tripName?: string;
  /**
   * Save (if needed) and resolve to the server id, for Share and export.
   * Absent → no Share button (older callers).
   */
  ensureSaved?: () => Promise<string | null>;
  /** @deprecated Share has its own button; pass ensureSaved. */
  onShare?: () => void;
  /** Duplicate / Delete for the ⋯ menu. */
  overflow?: Partial<TripOverflowProps>;
  onSave: () => void;
  /**
   * Save and say whether it worked: "Save, then start new" waits for it
   * before clearing. Absent → that choice isn't offered.
   */
  saveAsync?: () => Promise<boolean>;
  isLoggedIn: boolean;
  onSignIn: () => void;
  addWaypointMode: boolean;
  onToggleWaypointMode: () => void;
  onSearchAlongRoute?: (start: string, end: string, name: string, mids: string[], stops: TripStop[]) => void;
  routeName?: string;
  /** Clears the planner. Runs only after the confirm. */
  onClearTrip?: () => void;
  /** Kept for callers; popular trips are offered from the empty state only. */
  onOpenPopular?: () => void;
  onPrint: () => void;
  /** Opens the AI assistant. Present → "Ask AI" sits in the ⋯ menu. */
  onAskAssistant?: () => void;
  setTripStatusMessage: (msg: TripStatusMessage | null) => void;
  /** @deprecated Pass vehicleType. */
  vehicleLabel?: string;
  /** The rig being planned, for the guests' line ("Sign in for caravan routes…"). */
  vehicleType?: string;
  /** "3 stops · 1,019 km", under the trip's name at the top of the phone menu. */
  tripSummary?: string;
  /** Signed in: Plan my nights, from the menu's Trip group. */
  onPlanNights?: () => void;
  nightsOpen?: boolean;
  /** The card the share sheet draws: what the recipient will see. */
  sharePreview?: SharePreview;
  /** Phones: the planner bottom sheet (the pin-mode bar below). */
  sheet?: boolean;
  /**
   * Phones, pin armed: the bar's one primary becomes "Place at the
   * crosshair" (drop the stop where the map's crosshair points). Cancel sits
   * beside it as a text button and Save steps away until the mode ends.
   */
  onPlaceHere?: () => void;
  /** False while the map is still settling after the sheet drops. */
  placeReady?: boolean;
}

export const TRANSIENT_TOAST_MS = 2000;

/**
 * The status line. In-flow at the top of the footer: it takes its own space.
 *
 * `floating` (the phone sheet): a dark snackbar that hovers just above the
 * sheet instead, over the map, so it never costs the short sheet a row and
 * never covers the row it talks about. It leaves by itself (the host times
 * it out).
 */
export function TripStatusToast({
  message,
  onDismiss,
  floating = false,
}: {
  message: TripStatusMessage | null;
  onDismiss?: () => void;
  floating?: boolean;
}) {
  // A plain confirmation leaves on its own; a message with an action (Undo)
  // or a warning stays until it is used or dismissed.
  const transient = !!message && message.tone === 'success' && !message.action;
  React.useEffect(() => {
    if (!transient || !onDismiss) return;
    const t = window.setTimeout(onDismiss, TRANSIENT_TOAST_MS);
    return () => window.clearTimeout(t);
  }, [transient, message, onDismiss]);
  // What an add cost the drive ("+209 km · +3h 16m"), once the road lands.
  const cost = message?.cost ? (
    <span
      className={`whitespace-nowrap font-semibold tabular-nums ${message.costHeavy ? 'text-tp-warn' : ''}`}
      data-testid="trip-status-cost"
      data-heavy={message.costHeavy ? 'true' : undefined}
    >
      {' · '}
      {message.cost}
    </span>
  ) : null;
  if (floating) {
    return (
      <div
        aria-live="polite"
        role="status"
        // Fixed to the sheet's top edge (--tp-sheet-h, published by the
        // sheet on its parent): it hovers over the map, above the header.
        // 36px up: clear of Google's logo and terms riding the sheet's edge.
        className="pointer-events-none fixed inset-x-3 bottom-[calc(var(--tp-sheet-h,0px)+36px)] z-40"
        data-testid="trip-status-floating"
      >
        {message && (
          <div
            key={message.message}
            className={`pointer-events-auto rounded-tp-lg py-1.5 pl-3.5 pr-1.5 text-tp-sm font-medium text-white shadow-tp-pop animate-tp-enter motion-reduce:animate-none ${
              message.tone === 'error' ? 'bg-tp-danger' : message.tone === 'warning' ? 'bg-[#5c3a00]' : 'bg-tp-ink'
            }`}
            data-testid="trip-status"
          >
            <div className="flex min-h-[36px] items-center gap-2">
              {/* A neutral tick for every add. Only the figures turn amber,
                  and only for a real detour (see addCostIsHeavy). */}
              {message.tone === 'success' && <IconCheck className="h-4 w-4 flex-none text-white/80" />}
              <span className="min-w-0 flex-1">
                <span className="line-clamp-2 block leading-[18px]">{message.message}</span>
                {/* Line two is what it cost the drive. Until the road figures
                    land, where it went stands in. */}
                {(message.cost || message.detail) && (
                  <span className="block truncate text-tp-xs font-normal text-white/75">
                    {message.cost ? (
                      <span
                        className={`font-medium tabular-nums ${message.costHeavy ? 'text-[#ffc46b]' : 'text-white/80'}`}
                        data-testid="trip-status-cost"
                        data-heavy={message.costHeavy ? 'true' : undefined}
                      >
                        {message.cost}
                        {message.costHeavy && <span className="font-normal text-white/75"> · off your route</span>}
                      </span>
                    ) : (
                      message.detail
                    )}
                  </span>
                )}
              </span>
              {message.action && !message.secondaryAction && (
                <button
                  type="button"
                  onClick={message.action.onClick}
                  className="h-9 min-h-0 shrink-0 rounded-tp-sm border-0 bg-transparent px-2.5 text-tp-sm font-semibold text-[#9cc4ff] transition-colors duration-150 hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
                >
                  {message.action.label}
                </button>
              )}
              {onDismiss && (
                <button
                  type="button"
                  onClick={onDismiss}
                  aria-label="Dismiss"
                  className="flex h-9 w-9 min-h-0 shrink-0 items-center justify-center rounded-tp-sm border-0 bg-transparent text-white/70 transition-colors duration-150 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
                >
                  <IconX className="h-4 w-4" />
                </button>
              )}
            </div>
            {/* Two actions: their own row, the fix first, Undo last. */}
            {message.secondaryAction && (
              <div className="-mr-0.5 flex items-center justify-end gap-1 pb-0.5 pl-6">
                <button
                  type="button"
                  onClick={message.secondaryAction.onClick}
                  className="h-9 min-h-0 shrink-0 rounded-full border border-solid border-white/25 bg-white/10 px-3 text-tp-sm font-semibold text-white transition-colors duration-150 hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
                  data-testid="trip-status-secondary"
                >
                  {message.secondaryAction.label}
                </button>
                {message.action && (
                  <button
                    type="button"
                    onClick={message.action.onClick}
                    className="h-9 min-h-0 shrink-0 rounded-tp-sm border-0 bg-transparent px-2.5 text-tp-sm font-semibold text-[#9cc4ff] transition-colors duration-150 hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
                  >
                    {message.action.label}
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
  return (
    <div aria-live="polite" role="status">
      {message && (
        <Sheet
          key={message.message}
          className={`mb-2.5 flex min-h-[40px] items-center gap-2 rounded-tp border border-solid py-1 pl-3 pr-1 text-tp-sm font-medium ${STATUS_TONE[message.tone]}`}
          data-testid="trip-status"
        >
          <span className="min-w-0 flex-1">
            {/* A plain confirmation is one line (full text on hover); anything
                that asks something of you may take two. */}
            <span className={transient ? 'block truncate' : 'line-clamp-2 block'} title={transient ? message.message : undefined}>
              {message.message}
              {cost}
            </span>
            {message.detail && <span className="block truncate text-tp-xs font-normal opacity-80">{message.detail}</span>}
          </span>
          {message.secondaryAction && (
            <button
              type="button"
              onClick={message.secondaryAction.onClick}
              className="h-8 min-h-0 shrink-0 rounded-tp-sm border border-solid border-tp-line-strong bg-white px-2.5 text-tp-sm font-semibold text-tp-ink transition-colors duration-150 hover:border-tp-brand/50 hover:bg-tp-brand-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tp-brand/45"
              data-testid="trip-status-secondary"
            >
              {message.secondaryAction.label}
            </button>
          )}
          {message.action && (
            <button
              type="button"
              onClick={message.action.onClick}
              className="h-8 min-h-0 shrink-0 rounded-tp-sm border-0 bg-white/80 px-2.5 text-tp-sm font-semibold text-current transition-colors duration-150 hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current"
            >
              {message.action.label}
            </button>
          )}
          {onDismiss && (
            <button
              type="button"
              onClick={onDismiss}
              aria-label="Dismiss"
              className="flex h-8 w-8 min-h-0 shrink-0 items-center justify-center rounded-tp-sm border-0 bg-transparent text-current opacity-70 transition-opacity duration-150 hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current"
            >
              <IconX className="h-4 w-4" />
            </button>
          )}
        </Sheet>
      )}
    </div>
  );
}

// The overflow square: 40px (44px on phones), with a firm slate outline so
// it reads as a button on white, a step below the one filled primary.
const SQUARE = '!h-11 !w-11 flex-none !px-0 md:!h-10 md:!w-10 !border-[#94a3b8] hover:!border-tp-muted';

export default function TripActions(props: TripActionsProps) {
  const {
    tripStops,
    activeTripId,
    isSaved,
    isLoggedIn,
    tripName,
    ensureSaved,
    onSave,
    saveAsync,
    onSignIn,
    addWaypointMode,
    onToggleWaypointMode,
    onPrint,
    onAskAssistant,
    setTripStatusMessage,
    overflow,
    tripSummary,
    sharePreview,
    onPlanNights,
    nightsOpen,
    sheet = false,
  } = props;

  // Which confirm is up: "New trip" or the red "Clear trip".
  const [confirmKind, setConfirmKind] = React.useState<'new' | 'clear' | null>(null);
  const confirmOpen = confirmKind !== null;
  // The share sheet is a modal: the bar behind it rests (one blue in view).
  const [shareOpen, setShareOpen] = React.useState(false);
  const keepRef = React.useRef<HTMLButtonElement>(null);
  const [savingThenNew, setSavingThenNew] = React.useState(false);
  const hasTripStops = tripStops.length > 0;
  const canShare = tripStops.length >= 2;
  const name =
    tripName || (tripStops.length >= 2 ? `${tripStops[0].name} to ${tripStops[tripStops.length - 1].name}` : 'this trip');

  const exportItems =
    tripStops.length >= 2
      ? buildExportItems({ tripStops, activeTripId, isLoggedIn, onSignIn, onPrint, ensureSaved, setTripStatusMessage })
      : [];

  const toolItems: MenuItem[] = [
    // Ask AI only lands here when the planner has no add-a-stop box to
    // sit beside (HomeClient puts it there).
    ...(onAskAssistant
      ? [
          {
            key: 'ai',
            label: 'Ask AI about this trip',
            hint: 'Camps, fuel, what to see on the way',
            icon: <IconChat className={ROW_ICON} />,
            onSelect: onAskAssistant,
          },
        ]
      : []),
  ];

  const clear = props.onClearTrip ?? overflow?.onClearTrip;
  const moreItems = tripOverflowItems({
    hasTripStops,
    activeTripId,
    onClearTrip: () => setConfirmKind('new'),
    onClearStops: clear ? () => setConfirmKind('clear') : undefined,
    onDuplicate: overflow?.onDuplicate,
    onDelete: overflow?.onDelete,
    onPlanNights,
    nightsOpen,
    exportItems,
    toolItems,
  }).filter((i) => i.key !== 'new' || !!clear);

  const confirm =
    confirmKind === 'clear'
      ? clearTripConfirmCopy(tripStops.length)
      : newTripConfirmCopy({ name, stopCount: tripStops.length, activeTripId, isSaved });
  // Saved and unchanged: nothing left to save, so Share is the next thing
  // to do and takes the primary slot.
  const shareLeads = isSaved && canShare && !!ensureSaved;

  if (sheet && addWaypointMode && props.onPlaceHere) {
    // Phones, pin armed: one primary ("Place at the crosshair") and a plain
    // text Cancel. Save, Share and More step away so nothing competes with
    // the one thing the map is waiting for.
    return (
      <div className="flex w-full items-center gap-2" data-testid="trip-actions-bar" data-mode="pin">
        <button
          type="button"
          onClick={(e) => {
            onToggleWaypointMode();
            e.currentTarget.blur();
          }}
          aria-label="Cancel adding a stop on the map"
          className="h-12 min-h-0 flex-none rounded-full border-0 bg-transparent px-4 text-tp-base font-semibold text-tp-ink transition-colors duration-150 ease-tp-out [@media(hover:hover)]:hover:bg-tp-surface active:bg-tp-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tp-brand/45"
          data-testid="trip-pin-cancel"
        >
          Cancel
        </button>
        <Button
          variant="primary"
          size="md"
          onClick={props.onPlaceHere}
          disabled={props.placeReady === false}
          icon={<IconPinPlus className="h-4 w-4" />}
          className="!h-12 min-w-0 flex-1 !rounded-full !px-4 !text-tp-base !shadow-none"
          data-testid="pin-place-here"
        >
          Place at the crosshair
        </Button>
      </div>
    );
  }

  return (
    <>
      <div
        className={`flex w-full items-center gap-2 transition-[opacity,visibility] duration-150 ease-tp-out ${
          shareOpen ? 'pointer-events-none invisible opacity-0' : ''
        }`}
        data-testid="trip-actions"
        role="group"
        aria-label="Trip actions"
        {...(shareOpen ? { 'data-resting': '' } : {})}
      >
        {/* Saved and unchanged: no Save at all. The chip by the trip name
            says "Saved · just now"; Share takes the full width. */}
        {!shareLeads && (
          <Button
            key="save"
            variant="primary"
            size="md"
            onClick={onSave}
            disabled={tripStops.length < 2 || isSaved}
            title={
              tripStops.length < 2 ? 'Add a second stop to save the trip' : isSaved ? 'Saved. Nothing new to save.' : 'Save this trip'
            }
            icon={isSaved ? <IconCheck className="h-4 w-4" /> : undefined}
            className="!h-11 min-w-0 flex-1 md:!h-10"
            data-testid="trip-save"
          >
            {isSaved ? 'Saved' : activeTripId ? 'Save changes' : 'Save trip'}
          </Button>
        )}
        {/* Armed pin mode says so in words, with the way out. */}
        {addWaypointMode && (
          <Button
            variant="secondary"
            size="md"
            pressed
            onClick={onToggleWaypointMode}
            title="Stop dropping pins"
            icon={<IconPinPlus className="h-4 w-4 flex-none" />}
            className="!h-11 flex-none !border-tp-brand !bg-tp-brand-soft !text-tp-brand md:!h-10"
          >
            Done
          </Button>
        )}
        {ensureSaved && (
          <TripShareButton
            variant="bar"
            emphasis={shareLeads ? 'primary' : 'secondary'}
            ensureSaved={ensureSaved}
            isLoggedIn={isLoggedIn}
            onNeedSignIn={() => {
              onSignIn();
              setTripStatusMessage({ message: 'Sign in to share a link. Your trip comes with you.', tone: 'warning' });
            }}
            tripName={name}
            disabled={!canShare}
            disabledReason="Add a second stop to share the trip"
            preview={sharePreview}
            onOpenChange={setShareOpen}
          />
        )}
        {moreItems.length > 0 && (
          <Menu
            label="More"
            ariaLabel="More trip actions"
            iconOnly
            variant="secondary"
            size="md"
            placement="top"
            scrim
            sheetOnMobile
            sheetTitle={name}
            sheetSubtitle={tripSummary}
            icon={<IconMore className="h-5 w-5" />}
            triggerClassName={SQUARE}
            testId="trip-more"
            items={moreItems}
          />
        )}
      </div>
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmKind(null)}
        title={confirm.title}
        description={confirm.body}
        role="alertdialog"
        hideClose
        initialFocusRef={keepRef as React.RefObject<HTMLElement>}
        testId="new-trip-confirm"
      >
        {/* The safe ways are the buttons: save it first (filled) when it
            isn't saved, else start new, and "Keep planning" (outlined).
            Throwing work away is a red text button, set 16px further
            down, so it is never as easy to hit as the safe path. */}
        <div className="mt-5 flex flex-col gap-2.5">
          {confirm.saveFirst && saveAsync ? (
            <Button
              variant="primary"
              size="lg"
              disabled={savingThenNew}
              onClick={async () => {
                setSavingThenNew(true);
                const ok = await saveAsync();
                setSavingThenNew(false);
                if (!ok) return;
                setConfirmKind(null);
                clear?.();
              }}
              className="md:!h-10"
              data-testid="new-trip-save-first"
            >
              {savingThenNew ? 'Saving…' : confirm.saveFirst}
            </Button>
          ) : null}
          {!confirm.destructive && (
            <Button
              variant="primary"
              size="lg"
              onClick={() => {
                setConfirmKind(null);
                clear?.();
              }}
              className="md:!h-10"
              data-testid="new-trip-confirm-go"
            >
              {confirm.confirm}
            </Button>
          )}
          <Button
            ref={keepRef}
            variant="secondary"
            size="lg"
            onClick={() => setConfirmKind(null)}
            className="md:!h-10"
            data-testid="new-trip-keep"
          >
            Keep planning
          </Button>
          {confirm.destructive && (
            <Button
              variant="ghost"
              size="lg"
              onClick={() => {
                setConfirmKind(null);
                clear?.();
              }}
              className="mt-4 !text-tp-danger hover:!bg-tp-danger-soft hover:!text-tp-danger md:!h-10"
              data-testid="new-trip-confirm-go"
            >
              {confirm.confirm}
            </Button>
          )}
        </div>
      </Dialog>
    </>
  );
}
